/**
 * Homes a signed-out visitor saved from the /browse listing dialog, kept on
 * this device only. Saving does not ask for an account; signed-in buyers keep
 * saving to their account through the buyer-listing row instead.
 *
 * Storage can be missing or throw (private windows, blocked site data), so
 * every access is guarded and a failure reads as "nothing saved".
 */
const STORAGE_KEY = 'homepoint:savedHomes';

export interface DeviceSavedHome {
  mlsListingId: string;
  savedAt: string;
}

export const readDeviceSavedHomes = (): DeviceSavedHome[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is DeviceSavedHome =>
        typeof entry?.mlsListingId === 'string' &&
        typeof entry?.savedAt === 'string'
    );
  } catch {
    return [];
  }
};

export const isHomeSavedOnDevice = (mlsListingId: string): boolean =>
  readDeviceSavedHomes().some((home) => home.mlsListingId === mlsListingId);

/**
 * Adds or removes a home. Returns the saved state actually stored, so a
 * write that storage refuses leaves the bookmark showing what is true.
 */
export const setHomeSavedOnDevice = (
  mlsListingId: string,
  saved: boolean
): boolean => {
  const others = readDeviceSavedHomes().filter(
    (home) => home.mlsListingId !== mlsListingId
  );
  const next = saved
    ? [...others, { mlsListingId, savedAt: new Date().toISOString() }]
    : others;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    return saved;
  } catch {
    return isHomeSavedOnDevice(mlsListingId);
  }
};
